import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Container from "@saleor/components/Container";
import CouponFilterBar from "@saleor/components/CouponFilterBar";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import { Coupons_coupons_edges_node } from "@saleor/coupons/types/Coupons";
import { couponRankingListUrl } from "@saleor/coupons/url";
import { checkCouponPermissionAccess } from "@saleor/coupons/utils";
import { User_userPermissions } from "@saleor/fragments/types/User";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useNavigator from "@saleor/hooks/useNavigator";
import { sectionNames } from "@saleor/intl";
import { FilterPageProps, PageListProps, TabPageProps } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import CouponList from "../CouponList";
import {
  CouponFilterKeys,
  CouponListUrlFilterOpts,
  createFilterStructure
} from "./filters";

export interface CouponListPageProps
  extends PageListProps,
    TabPageProps,
    FilterPageProps<CouponFilterKeys, CouponListUrlFilterOpts> {
  coupons: Coupons_coupons_edges_node[];
  onSubmit: (data) => SubmitPromise;
  navigateToRanking: () => void;
  onClickCouponRedemption?: (id?: string) => () => void;
  onViewMalls: (id: string) => () => void;
  userPermissions: User_userPermissions[];
  activeTab;
  userType?: boolean;
}

export const CouponListPage: React.FC<CouponListPageProps> = ({
  activeTab,
  coupons,
  currentTab,
  filterOpts,
  navigateToRanking,
  initialSearch,
  onSearchChange,
  onFilterChange,
  onAll,
  onTabChange,
  onTabDelete,
  onTabSave,
  onViewMalls,
  tabs,
  userType,
  currencySymbol,
  userPermissions,
  onAdd,
  onSubmit,
  ...listProps
}) => {
  const intl = useIntl();

  const breadcrumbDirectory = [
    {
      name: "Coupons"
    }
  ];

  const featuredDealsCoupons = coupons.filter(
    coupon => coupon.dealType === "DEALS" && coupon.isFeatured
  );

  const featuredEventsCoupons = coupons.filter(
    coupon => coupon.dealType === "EVENTS" && coupon.isFeatured
  );

  const userHasFeatureCouponAccess = Boolean(
    checkCouponPermissionAccess(userPermissions, "FEATURE_COUPONS")
  );
  const userHasCreateCouponAccess = Boolean(
    checkCouponPermissionAccess(userPermissions, "ADD_COUPON")
  );

  const allowFeatureCouponList = Boolean(
    (featuredDealsCoupons.length > 0 || featuredEventsCoupons.length > 0) &&
      userHasFeatureCouponAccess
  );

  const location = useLocation();
  const navigate = useNavigator();
  const structure = createFilterStructure(intl, filterOpts);

  const handleViewCouponRankingClick = () => {
    const currentSearchParams = location.search;
    const cleanSearchParams = currentSearchParams.startsWith("?")
      ? currentSearchParams.slice(1)
      : currentSearchParams;
    const rankingListUrl = couponRankingListUrl().endsWith("?")
      ? couponRankingListUrl().slice(0, -1)
      : couponRankingListUrl();
    navigate(`${rankingListUrl}?${cleanSearchParams}`);
  };

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <PageHeader title={intl.formatMessage(sectionNames.coupons)}>
        {allowFeatureCouponList && (
          <Button
            variant="outlined"
            color="primary"
            style={{ marginRight: "8px" }}
            onClick={handleViewCouponRankingClick}
            id="couponRankingBtn"
          >
            <span style={{ color: "#1848FE", fontWeight: "bold" }}>
              <FormattedMessage
                defaultMessage="View Coupon Ranking"
                description="button"
              />
            </span>
          </Button>
        )}
        {userHasCreateCouponAccess && (
          <Button
            color="primary"
            variant="contained"
            onClick={onAdd}
            id="createCouponsBtn"
            style={{ backgroundColor: "#1848FE" }}
          >
            <span style={{ color: "#ffffff", fontWeight: "bold" }}>
              <FormattedMessage
                defaultMessage="Create Coupon"
                description="button"
              />
            </span>
          </Button>
        )}
      </PageHeader>
      <Card>
        <CouponFilterBar
          currencySymbol={currencySymbol}
          currentTab={currentTab}
          initialSearch={initialSearch}
          tabs={tabs}
          filterStructure={structure}
          searchPlaceholder={intl.formatMessage({
            defaultMessage: "Search Coupons"
          })}
          onSearchChange={onSearchChange}
          onFilterChange={onFilterChange}
          onTabChange={onTabChange}
          onTabDelete={onTabDelete}
          disablePlusOneTab={true}
          onTabSave={onTabSave}
        />
        {coupons && (
          <CouponList
            userPermissions={userPermissions}
            userHasFeatureCouponAccess={userHasFeatureCouponAccess}
            coupons={coupons}
            onSubmit={onSubmit}
            onViewMalls={onViewMalls}
            {...listProps}
          />
        )}
      </Card>
    </Container>
  );
};

export default CouponListPage;
